/*
 * Copyright © 2024 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import {NgTemplateOutlet} from '@angular/common';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    ContentChild,
    ElementRef,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {Nullish} from '@dv/shared/code';
import {TranslocoDirective} from '@jsverse/transloco';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {debounceTime, filter, map, pairwise, Subscription, tap} from 'rxjs';
import {EditModeDirective} from '../directives/edit-mode.directive';

@Component({
    selector: 'dvlib-click-2-edit',
    templateUrl: './click-2-edit.component.html',
    styleUrl: './click-2-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, TooltipModule, TranslocoDirective, FormsModule],
})
export class Click2EditComponent {

    public text = input.required<string | Nullish>();
    public label = input.required<string>();
    public multiline = input(false, {transform: booleanAttribute});
    public enabled = input(true, {transform: booleanAttribute});

    public submitted = signal(false);

    public readonly save = output<void>();

    public content = computed(() => this.text() || this.label());

    @ContentChild(EditModeDirective) public readonly editModeDirective!: EditModeDirective;

    private elementRef: ElementRef<HTMLElement> = inject(ElementRef);

    public editMode = signal(false);

    // noinspection JSUnusedLocalSymbols
    private readonly resetFocusToButtonAfterEdit: Subscription = toObservable(this.editMode)
        .pipe(
            pairwise(),
            map(([oldValue, newValue]) => oldValue && !newValue),
            filter(Boolean),
            debounceTime(0),
            tap(() => this.elementRef.nativeElement.querySelector('button')?.focus()),
            takeUntilDestroyed(),
        ).subscribe();

    public markAsSubmitted(): void {
        this.submitted.set(true);
    }

    public displayEditMode(): void {
        this.editMode.set(true);
        this.submitted.set(false);
    }

    public saveEdit(): void {
        this.save.emit();
        this.editMode.set(false);
        this.submitted.set(true);
    }

    public cancelEdit(): void {
        this.editMode.set(false);
        this.submitted.set(false);
    }
}
